<template>
  <seo-tour-block
    :text="seoText"
    :faq="seoFaq"
    class="seo-block--tour"
    :slug-info="slugInfo"
    :other-tours-to-city-list="otherToursToCityList"
    :other-tours-from-city-list="otherToursFromCityList"
    :default-sogu-fields="props.defaultSoguFields"
    :tags="hiddenTags"
    :route="route"
    :current-tag="selectedTagSlug"
  />
</template>

<script setup lang="ts">
  import { SeoTourBlock } from '@experience/travelers-seo-block-package';
  import { useTourSeoInfo } from '@/entities/region/api/useTourSeoInfo.ts';
  import { computed } from 'vue';
  import { type FromCityPayload, LocationType } from '@/entities/location';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import type { CityTag, RegionTag } from '@/shared/api/experience';
  import { useRegionTag } from '@/entities/tag/api/useRegionTag.ts';
  import { useCityTag } from '@/entities/tag/api/useCityTag.ts';
  import { type Params, useExperienceAggregations } from '@/entities/experience/aggregation';
  import { tourFiltersToQuery, useTourFiltersStore } from '@/entities/filter/store/tour';
  import { useCountryInfo } from '@/entities/country/api/useCountryInfo.ts';
  import { useCityInfo } from '@/entities/city/api/useCityInfo.ts';
  import { useRegionInfo } from '@/entities/otherTours/api/useRegionInfo.ts';
  import { useCityToursInRegion } from '@/entities/otherTours/api/useCityToursInRegion.ts';
  import type { OtherToursItem } from '@/entities/otherTours/types/OtherToursItem.ts';
  import { useCountryFromCitiesInfo } from '@/entities/country/api/useCountryFromCitiesInfo.ts';
  import { useSchemaFaq } from '@/widgets/seoBlock/composable/useSchemaFaq.ts';
  import { useTourInRegionFromCitySeoInfo } from '@/entities/region/api/useTourInRegionFromCitySeoInfo.ts';
  import { useTourInCountryFromCitySeoInfo } from '@/entities/region/api/useTourInCountryFromCitySeoInfo.ts';
  import { useRegionTagSeoInfo } from '@/entities/region/api/useRegionTagSeoInfo.ts';
  import { useCountryTagSeoInfo } from '@/entities/region/api/useCountryTagSeoInfo';
  import type { ExtendedPartTagType } from '@/entities/rubrics';
  import { useTourCitySeoInfo } from '@/entities/city/api/useTourCitySeoInfo.ts';
  import { useTourCityTagSeoInfo } from '@/entities/city/api/useTourCityTagSeoInfo.ts';

  interface Props {
    slug: string;
    location: LocationType;
    fromCitySeoPayload?: Nullable<FromCityPayload>;
    defaultSoguFields: ContextGeoSubject;
    currentTag: ExtendedPartTagType | null;
  }

  const props = defineProps<Props>();

  const route = useRoute();
  const filterStore = useTourFiltersStore();

  const selectedTagSlug = computed(() => {
    const slug = filterStore.searchState.tag__slugs;

    return slug && slug !== 'all' ? slug : '';
  });

  const typeTour = computed(() => {
    return props.location === LocationType.REGION ? 'region_tours' : 'country_tours';
  });

  const isTourTagPage = computed(() => {
    return Boolean(props.currentTag?.value && props.currentTag?.value !== 'all');
  });

  const { tourSeoData } = useTourSeoInfo(
    computed(() => typeTour.value),
    computed(() => props.slug),
    computed(() => props.location !== LocationType.CITY),
  );

  const { tourCitySeoData } = useTourCitySeoInfo(
    computed(() => props.slug),
    computed(() => props.location === LocationType.CITY && !isTourTagPage.value),
  );

  const { cityTagSeoData } = useTourCityTagSeoInfo(
    computed(() => props.slug),
    computed(() => props.currentTag?.value || ''),
    computed(() => props.location === LocationType.CITY && isTourTagPage.value),
  );

  const { countryTagSeoData } = useCountryTagSeoInfo(
    computed(() => `${props.slug}__${props.currentTag?.value}`),
    computed(() => props.location === LocationType.COUNTRY && isTourTagPage.value),
  );

  const { tourInRegionFromCitySeoData } = useTourInRegionFromCitySeoInfo(
    computed(() => props.fromCitySeoPayload?.regionSlug),
    computed(() => props.fromCitySeoPayload?.citySlug),
    computed(() => !!props.fromCitySeoPayload),
  );

  const { tourInCountryFromCitySeoData } = useTourInCountryFromCitySeoInfo(
    computed(() => props.fromCitySeoPayload?.countrySlug),
    computed(() => props.fromCitySeoPayload?.citySlug),
    computed(() => !!props.fromCitySeoPayload),
  );

  const { regionTagSeoData } = useRegionTagSeoInfo(
    computed(() => `${props.slug}__${props.currentTag?.value}`),
    computed(() => props.location === LocationType.REGION && isTourTagPage.value),
  );

  const seoText = computed(() => {
    if (props.location === LocationType.CITY && isTourTagPage.value) {
      return cityTagSeoData.value?.seo?.text || '';
    }

    if (props.location === LocationType.CITY) {
      return tourCitySeoData.value?.seo?.text || '';
    }

    if (
      props.location === LocationType.COUNTRY &&
      !props.fromCitySeoPayload &&
      props.currentTag?.value !== 'all' &&
      props.currentTag !== null
    ) {
      return countryTagSeoData.value?.seo?.text || '';
    }

    if (
      props.location === LocationType.REGION &&
      !props.fromCitySeoPayload &&
      props.currentTag?.value !== 'all' &&
      props.currentTag !== null
    ) {
      return regionTagSeoData.value?.seo?.text || '';
    }

    if (props.location === LocationType.REGION) {
      return !!props.fromCitySeoPayload
        ? tourInRegionFromCitySeoData.value?.seo?.text || ''
        : tourSeoData.value?.seo?.text || '';
    }

    if (props.location === LocationType.COUNTRY) {
      return !!props.fromCitySeoPayload
        ? tourInCountryFromCitySeoData.value?.seo?.text || ''
        : tourSeoData.value?.seo?.text || '';
    }

    return '';
  });

  const seoFaq = computed(() => {
    if (props.location === LocationType.CITY && isTourTagPage.value) {
      return cityTagSeoData.value?.faq || {};
    }

    if (props.location === LocationType.CITY) {
      return tourCitySeoData.value?.faq || {};
    }

    return (isTourTagPage.value ? regionTagSeoData.value?.faq : tourSeoData.value?.faq) || {};
  });

  const { countryInfo } = useCountryInfo(
    computed(() => props.slug),
    computed(() => props.location === LocationType.COUNTRY),
  );

  const { cityInfo } = useCityInfo(
    computed(() => props.slug),
    computed(() => props.location === LocationType.CITY),
  );

  const { regionInfo } = useRegionInfo(
    computed(() => props.slug),
    computed(() => props.location === LocationType.REGION),
  );

  const { regionTags } = useRegionTag({
    slug: computed(() => props.slug),
    enabled: computed(() => props.location === LocationType.REGION),
  });

  const { cityTags } = useCityTag({
    slug: computed(() => props.slug),
    sorting: computed(() => '-tours_count'),
    enabled: computed(() => props.location === LocationType.CITY),
  });

  const slugInfo = computed(() => {
    if (props.location === LocationType.CITY) {
      return cityInfo.value || {};
    }

    const currentInfo = props.location === LocationType.REGION ? regionInfo.value : countryInfo.value;

    return currentInfo || {};
  });

  const aggregationParams = computed<Params>((): Params => {
    const pageParams = {
      city: props.location === LocationType.CITY ? props.slug : undefined,
      region: props.location === LocationType.REGION ? props.slug : undefined,
      country: props.location === LocationType.COUNTRY ? props.slug : undefined,
    };

    return {
      ...tourFiltersToQuery(filterStore.searchState, pageParams),
      page: computed(() => 1),
    } as Params;
  });

  const { tags } = useExperienceAggregations(aggregationParams);

  type SeoHiddenTag = CityTag | RegionTag;

  const buildCityTagToursUrl = (tag: SeoHiddenTag) => {
    if (props.location !== LocationType.CITY || !tag.slug) {
      return '';
    }

    return `/tours/city/${props.slug}/${tag.slug}/`;
  };

  const modifyTagsWithAggregation = (arr: SeoHiddenTag[]) => {
    return arr
      .map((item) => {
        const id = item?.id || 0;
        const toursUrl = item.urls?.tours || buildCityTagToursUrl(item);

        return {
          ...item,
          counter: tags.value?.[id] || 0,
          urls: {
            ...item.urls,
            tours: toursUrl,
          },
        };
      })
      .filter((tag) => !!tag?.counter);
  };

  const hiddenTags = computed(() => {
    const arr =
      (props.location === LocationType.CITY ? cityTags.value : regionTags.value)?.reduce((acc, tag) => {
        if (
          (tag.flags?.is_hidden || tag.flags?.is_seo_only) &&
          !tag.flags?.is_attraction &&
          (tag.counters?.tours_count ?? 0) > 0
        ) {
          acc.push(tag);
        }

        return acc;
      }, [] as SeoHiddenTag[]) || [];

    return modifyTagsWithAggregation(arr);
  });

  const { cityToursInRegion } = useCityToursInRegion(
    computed(() => props.slug),
    computed(() => props.location === LocationType.REGION),
  );

  const otherTourToCityLink = (slug: string) => {
    const { country } = route.params;

    return `/tours/${country}/category/${slug}/`;
  };
  const otherToursToCityList = computed(() => {
    if (props.location === LocationType.CITY || !cityToursInRegion.value) {
      return [];
    }

    return cityToursInRegion.value?.reduce((acc: OtherToursItem[], item) => {
      if (!item.slug) {
        return acc;
      }

      acc.push({
        slug: item.slug,
        text: item.title,
        link: otherTourToCityLink(item.slug),
      });

      return acc;
    }, [] as OtherToursItem[]);
  });

  const { countryFromCitiesInfo } = useCountryFromCitiesInfo(
    computed(() => props.slug),
    computed(() => props.location === LocationType.COUNTRY),
  );

  const fromCitiesList = computed(() => {
    if (props.location === LocationType.CITY) {
      return [];
    }

    return props.location === LocationType.COUNTRY ? countryFromCitiesInfo.value : regionInfo.value?.from_cities;
  });

  // TODO: вынести в миксин ?
  const otherTourFromCityLink = (slug: string) => {
    const { country, regionSlug, countrySlug } = route.params;

    return props.location === LocationType.REGION
      ? `/tours/${country}/${regionSlug}/iz/${slug}/`
      : `/tours/${countrySlug}/iz/${slug}/`;
  };

  const otherToursFromCityList = computed(() => {
    if (!fromCitiesList.value) {
      return [];
    }

    return fromCitiesList.value?.reduce((acc: OtherToursItem[], item) => {
      if (!item.slug || !item.id) {
        return acc;
      }

      acc.push({
        slug: item.slug || '',
        text: `Туры ${slugInfo.value?.across_obj_phrase} ${item?.from_obj_phrase}`,
        link: otherTourFromCityLink(item.slug),
      });

      return acc;
    }, []);
  });

  useSchemaFaq(seoFaq);
</script>

<style scoped lang="scss">
  :deep(.seo-block__inner li) {
    list-style: disc;
  }
</style>
